<script setup lang="ts">
import { useEventsApi } from '~/composables/api/use-events-api';
import { useMyInterests } from '~/stores/use-my-interests';

useHead(
  {
    title: 'Search Events | Arvee Events',
    meta: [
      { name: 'robots', content: 'noindex, nofollow' },
      {
        name: 'description',
        content: 'Search for community-driven RV events.',
      },
    ],
  },
  { tagPriority: 10 },
);

const config = useRuntimeConfig();
const route = useRoute();

const inputValue = ref((route.query.q as string) ?? '');
let debounceTimer: ReturnType<typeof setTimeout> | undefined;

watch(inputValue, (val) => {
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    const trimmed = (val ?? '').trim();
    if (trimmed.length >= 2) {
      navigateTo({ path: '/search', query: { q: trimmed } });
    } else {
      navigateTo({ path: '/search' });
    }
  }, 300);
});

// Back-sync: URL → input (browser back/forward)
watch(
  () => route.query.q,
  (q) => {
    const val = (q as string) ?? '';
    if (val !== inputValue.value) {
      inputValue.value = val;
    }
  },
);

onUnmounted(() => {
  clearTimeout(debounceTimer);
});

// Warm the shared interest store so search-result cards' interest toggles seed
// their current rung once auth settles. Client + authed only; the store dedupes.
onMounted(() => {
  useMyInterests().ensureUpcoming();
});

const queryParam = computed(() => {
  const q = ((route.query.q as string) ?? '').trim();
  return q.length >= 2 ? q : null;
});

const currentPage = computed(() => {
  const p = Number(route.query.page);
  return p > 0 ? p : 1;
});

const { data, status, refresh } = useAsyncData(
  'search-events',
  () => {
    if (!queryParam.value) return Promise.resolve(null);
    const params: Record<string, unknown> = { q: queryParam.value };
    if (currentPage.value > 1) params.page = currentPage.value;
    return useEventsApi().search(params);
  },
  {
    watch: [() => route.query],
    server: !config.public.mock,
  },
);

const { trackSiteSearch } = useMatomo();

watch([data, status], () => {
  if (status.value === 'success' && queryParam.value) {
    trackSiteSearch(queryParam.value, false, data.value?.meta?.total ?? 0);
  }
});

function setPage(page: number) {
  navigateTo({
    query: {
      ...route.query,
      page: page > 1 ? page : undefined,
    },
  });
}

function onClear() {
  inputValue.value = '';
}
</script>

<template>
  <div>
    <h1 class="text-h4 mb-6">Search Events</h1>

    <v-text-field
      v-model="inputValue"
      aria-label="Search events"
      prepend-inner-icon="mdi-magnify"
      placeholder="Search events..."
      clearable
      variant="outlined"
      class="mb-6"
      @click:clear="onClear"
    />

    <!-- No query entered -->
    <div v-if="!route.query.q && !inputValue?.trim()" class="text-center py-12">
      <v-icon size="64" color="medium-emphasis" class="mb-4">
        mdi-magnify
      </v-icon>
      <p class="text-h6 text-medium-emphasis">Search for events</p>
      <p class="text-body-2 text-medium-emphasis">
        Enter at least 2 characters to search.
      </p>
    </div>

    <!-- Query too short -->
    <div
      v-else-if="!queryParam && inputValue?.trim()"
      class="text-center py-12"
    >
      <v-icon size="64" color="medium-emphasis" class="mb-4">
        mdi-magnify
      </v-icon>
      <p class="text-h6 text-medium-emphasis">Keep typing...</p>
      <p class="text-body-2 text-medium-emphasis">
        Enter at least 2 characters to search.
      </p>
    </div>

    <!-- Loading State -->
    <v-row v-else-if="status === 'pending' || status === 'idle'">
      <v-col v-for="n in 6" :key="n" cols="12" sm="6" md="4">
        <v-skeleton-loader type="card" />
      </v-col>
    </v-row>

    <!-- Error State -->
    <v-alert
      v-else-if="status === 'error'"
      type="error"
      variant="tonal"
      class="mb-6"
    >
      <div class="d-flex align-center justify-space-between">
        <span>Failed to load search results. Please try again.</span>
        <v-btn variant="text" color="error" @click="refresh()"> Retry </v-btn>
      </div>
    </v-alert>

    <!-- Empty Results -->
    <div v-else-if="!data?.data.length" class="text-center py-12">
      <v-icon size="64" color="medium-emphasis" class="mb-4">
        mdi-calendar-blank-outline
      </v-icon>
      <p class="text-h6 text-medium-emphasis">No events matching your search</p>
      <p class="text-body-2 text-medium-emphasis">
        Try a different search term.
      </p>
    </div>

    <!-- Results Grid -->
    <template v-else>
      <v-row>
        <v-col
          v-for="event in data.data"
          :key="event.id"
          cols="12"
          sm="6"
          md="4"
        >
          <EventsEventCard :event="event" />
        </v-col>
      </v-row>

      <!-- Pagination -->
      <div v-if="data.meta.last_page > 1" class="d-flex justify-center mt-8">
        <v-pagination
          :model-value="data.meta.current_page"
          :length="data.meta.last_page"
          @update:model-value="setPage"
        />
      </div>
    </template>
  </div>
</template>
